<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import ProductCardSearch from '../product-card/ProductCardSearch.vue';
import Button from '../../mixit-ui/components/button/Button.vue';
import ItemCategory from '../list-item/ItemCategory.vue';
import BlogCardSearch from '../blog-card/BlogCardSearch.vue';
import type { SearchProduct, SearchCategory, SearchArticle } from '@/services-new/searchService';
import type { ProductCategory } from '@/services-new/categoryService';

const props = defineProps<{
  isOpen: boolean;
  isMobile?: boolean;
  mobileMaxHeight?: string;
  products: SearchProduct[];
  categories: SearchCategory[];
  articles: SearchArticle[];
  topSearches: string[];
  crossSellProducts: SearchProduct[];
  idleCategories: ProductCategory[];
  isSearchActive: boolean;
  isSearchLoading: boolean;
  query: string;
}>();

// Products to display and the heading for the products section
const displayedProducts = computed(() =>
  props.isSearchActive && props.products.length > 0
    ? props.products
    : props.crossSellProducts
)
const productsTitle = computed(() =>
  props.isSearchActive && props.products.length > 0
    ? null
    : 'Nejoblíbenější produkty'
)

// True when a search returned no products (and we're not still loading)
const noProductsFound = computed(() =>
  props.isSearchActive && !props.isSearchLoading && props.products.length === 0
)

// Top searches visible only in idle state or when search returned no products
const showTopSearches = computed(() =>
  props.topSearches.length > 0 && (!props.isSearchActive || noProductsFound.value)
)

// Categories: always shown (idle uses idleCategories, search uses categories)
const showCategoriesSection = computed(() => props.isSearchActive || props.idleCategories.length > 0)
const showArticlesSection = computed(() => props.isSearchActive)

const emit = defineEmits<{
  (e: 'update:onHide', value: boolean): void;
}>();

watch(() => props.isOpen, (open) => {
  if (open) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }
}, { immediate: true })

let resizeObserver: ResizeObserver | null = null;
const searchBox = ref<HTMLElement | null>(null)

function updatePopperSize() {
  const searchHeaderWrap = document.getElementById('searchHeaderWrap') as HTMLElement;
  if (searchBox.value && searchHeaderWrap) {
    if (window.innerWidth >= 1560) {
      searchBox.value.style.width = `${searchHeaderWrap.offsetWidth}px`
    } else if (window.innerWidth >= 1320) {
      searchBox.value.style.width = `${searchHeaderWrap.offsetWidth*1.5}px`
    } else if (window.innerWidth >= 1184) {
      searchBox.value.style.width = `${searchHeaderWrap.offsetWidth*2}px`
    } else searchBox.value.style.width = `${window.innerWidth}px`
    searchBox.value.style.maxHeight = `${innerHeight - (searchHeaderWrap.getBoundingClientRect().top + searchHeaderWrap.offsetHeight + 36)}px`
  }
}

onMounted(() => {
  if (!props.isMobile) {
    const searchHeaderWrap = document.getElementById('searchHeaderWrap') as HTMLElement;
    if (searchHeaderWrap) {
      resizeObserver = new ResizeObserver(updatePopperSize);
      resizeObserver.observe(searchHeaderWrap);
    }
    window.addEventListener('resize', updatePopperSize);
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect();
  if (!props.isMobile) window.removeEventListener('resize', updatePopperSize);
  document.body.classList.remove('overflow-hidden');
})
</script>

<template>
  <div
    ref="searchBox"
    class="bg-yellow-50 2lg:rounded-lg p-4 md:p-6 gap-6 overflow-auto flex flex-col 2lg:max-h-none"
    :style="isMobile && mobileMaxHeight ? { height: mobileMaxHeight } : undefined"
  >
    <!-- Loading spinner -->
    <div
      v-if="isSearchLoading && isSearchActive"
      class="flex flex-col items-center gap-3 py-4"
    >
      <svg
        class="animate-spin w-8 h-8 text-brown-400"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <p class="text-sm font-roboto-slab text-brown-400">
        Hledáme…
      </p>
    </div>

    <!-- No products found message -->
    <div
      v-if="noProductsFound"
      class="flex flex-col items-center gap-2 py-2"
    >
      <i class="mi-search-err text-4xl text-brown-300" />
      <p class="text-sm font-roboto-slab font-bold text-brown-600 text-center">
        Pro hledaný výraz jsme nenašli žádné produkty.
      </p>
    </div>

    <!-- Products section (search results or cross-sell) -->
    <div
      v-if="!isSearchLoading && displayedProducts.length"
      class="flex flex-col gap-4"
    >
      <h6
        v-if="productsTitle"
        class="text-brown-600"
      >
        {{ productsTitle }}
      </h6>
      <div class="gap-1 flex flex-col">
        <ProductCardSearch
          v-for="product in displayedProducts"
          :key="product.id"
          :product="product"
        />
      </div>
      <Button
        v-if="isSearchActive && products.length > 0"
        label="Zobrazit všechny"
        size="md"
        class="w-full"
        :link-to="{ name: 'searchPath', params: { search: query } }"
      />
    </div>

    <!-- Top searches — idle state or no product results -->
    <div
      v-if="showTopSearches"
      class="flex flex-col gap-4"
    >
      <h6 class="text-brown-600">
        Nejhledanější výrazy
      </h6>
      <div class="flex w-full gap-2 flex-wrap">
        <Button
          v-for="(phrase, index) in topSearches"
          :key="index"
          size="sm"
          behaviour="responsive"
          severity="secondary"
          :label="phrase"
          :link-to="{ name: 'searchPath', params: { search: phrase } }"
        />
      </div>
    </div>

    <!-- Categories — idle shows first 6, search shows results -->
    <div
      v-if="showCategoriesSection && !isSearchLoading"
      class="flex flex-col gap-4"
    >
      <h6 class="text-brown-600">
        Kategorie
      </h6>
      <!-- Idle state: show first 6 from categoryService -->
      <div
        v-if="!isSearchActive"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1"
      >
        <ItemCategory
          v-for="cat in idleCategories"
          :key="cat.id"
          size="sm"
          :label="cat.title"
          :image-uri="cat.image"
          :router-to="cat.permalink"
        />
      </div>
      <!-- Search state: show search result categories -->
      <div
        v-else-if="categories.length"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1"
      >
        <ItemCategory
          v-for="cat in categories"
          :key="cat.id"
          size="sm"
          :label="cat.title"
          :image-uri="cat.iconUrl"
          :router-to="cat.permalink"
        />
      </div>
      <div
        v-else-if="isSearchActive"
        class="flex items-center gap-2"
      >
        <i class="mi-search-err text-xl text-brown-300" />
        <p class="text-sm font-roboto-slab font-bold text-brown-400 italic">
          V kategoriích jsme nic nenašli.
        </p>
      </div>
    </div>

    <!-- Articles — only while searching and not loading -->
    <div
      v-if="showArticlesSection && !isSearchLoading"
      class="flex flex-col gap-4"
    >
      <h6 class="text-brown-600">
        Články
      </h6>
      <div
        v-if="articles.length"
        class="flex flex-col gap-1"
      >
        <BlogCardSearch
          v-for="article in articles"
          :key="article.id"
          :article="article"
        />
      </div>
      <div
        v-else
        class="flex items-center gap-2"
      >
        <i class="mi-search-err text-xl text-brown-300" />
        <p class="text-sm font-roboto-slab font-bold text-brown-400 italic">
          Nenašli jsme žádné články.
        </p>
      </div>
    </div>
  </div>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-brown-800/50 z-10"
        @click="emit('update:onHide', false)"
      />
    </Transition>
  </Teleport>
</template>
